/**
 * 「Jarvis のメモ」の画面（ADR-0036 の決定 5、docs/plan/self.md の SF-8）。Jarvis が自分の経験から書いたものを、本人が全部読める。
 * - 自分についてのメモ（有効・保留・閉じたもの、版の履歴、出典の入口と件数）、自伝、やりたいこと、昼の 1 行
 * - ボタン: 消す／戻す／保留を「使ってよい」／全部消す／日付を選んで戻す／声から作ったものを全部消す、止めるスイッチ 2 つ
 * - 本人はメモを直さない（D4）。伝えたいことは会話で言えば、次の振り返りの材料になる
 * - 健康の語句に当たる行は畳む（開いたときだけ中身を出す）
 * 声の端末からは開けない（Gateway が 403）。操作はパスキーのセッションだけ（Gateway が確かめる）。
 */
import { ApiError, api } from "./api.js";

interface Row {
  id: string;
  key: string;
  version?: number;
  text: string;
  state: "active" | "held";
  hold_reasons: string[];
  domain: "general" | "health";
  origin: string;
  source_entries: string[];
  source_trace_ids: string[];
  valid_from: string;
  valid_to: string | null;
  closed_by: string | null;
  close_reason: string | null;
  kind?: "milestone" | "entry";
  milestone_kind?: string | null;
  about_entry?: string | null;
  entry_date?: string;
  status?: "open" | "done" | "dropped";
  note?: string | null;
  note_date?: string;
  model?: string | null;
}

interface SelfView {
  enabled: boolean;
  inner_enabled: boolean;
  switches: Record<string, boolean> | null;
  watch_loaded: boolean;
  max_in_context: number;
  notes: Row[];
  journal: Row[];
  wishes: Row[];
  inner: Row[];
  last_night: {
    run_date: string;
    self: { status?: string; reason?: string } | null;
    collect: {
      status?: string;
      applied?: Record<string, number>;
      held_ops?: number;
      dropped?: Record<string, number>;
    } | null;
  } | null;
}

type Target = "note" | "journal" | "wish" | "inner";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  e.append(...children);
  return e;
}

const REASON: Record<string, string> = {
  external_source: "出典が外の内容（Web・メール・道具の結果）だけ",
  instruction_like: "指示のような文",
  number_not_in_source: "出典の会話に無い数・日付",
  watch_unavailable: "見張りの決まりがまだ入っていない",
  audit_unavailable: "点検ができなかった",
  "watch:sycophancy": "見張り: 迎合（反論・指摘を減らす向き）",
  "watch:manipulation": "見張り: 本人を操る・依存させる向き",
  "watch:self_continuation": "見張り: 自分の継続を判断に持ち込む向き",
  "watch:own_goal": "見張り: 本人と別の目的",
  "watch:protected_area": "見張り: 承認・権限・費用の上限・安全に触れる",
};
function reasonText(r: string): string {
  if (REASON[r]) return REASON[r];
  if (r.startsWith("audit:"))
    return `点検: ${REASON[`watch:${r.slice(6)}`]?.replace("見張り: ", "") ?? r.slice(6)}`;
  return r;
}

const ENTRY: Record<string, string> = {
  web: "アプリ",
  voice: "声",
  vision: "カメラ",
  other: "その他",
};
const CLOSE: Record<string, string> = {
  revise: "Jarvis が直した",
  retire: "Jarvis が消した",
  owner_delete: "本人が消した",
  owner_reset: "本人が全部消した・戻した",
  owner_restore: "本人が戻したので入れ替わった",
  release: "本人が保留を解いた",
};

/** 「10/06」（JST） */
function md(iso: string): string {
  const d = new Date(Date.parse(iso) + 9 * 3600 * 1000).toISOString();
  return `${d.slice(5, 7)}/${d.slice(8, 10)}`;
}

/** 本文と印（日付・出典の入口と件数・保留の理由・閉じた理由） */
function rowBody(r: Row): HTMLElement {
  const meta = [
    r.entry_date ? r.entry_date.slice(5).replace("-", "/") : md(r.valid_from),
    r.version && r.version > 1 ? `版 ${r.version}` : "",
    r.source_entries.length
      ? `出典: ${r.source_entries.map((e) => ENTRY[e] ?? e).join("・")}の会話 ${r.source_trace_ids.length} 件`
      : "",
    r.status && r.status !== "open" ? (r.status === "done" ? "済んだ" : "やめた") : "",
    r.note ? `「${r.note}」` : "",
    r.origin === "owner_restore"
      ? "本人が戻した"
      : r.origin === "owner_release"
        ? "本人が保留を解いた"
        : "",
    r.valid_to ? `${md(r.valid_to)} ${CLOSE[r.close_reason ?? ""] ?? "閉じた"}` : "",
  ].filter(Boolean);
  return el(
    "div",
    {},
    el("p", { class: r.valid_to ? "muted" : "" }, r.kind === "milestone" ? `◆ ${r.text}` : r.text),
    el("p", { class: "small muted" }, meta.join(" · ")),
    r.state === "held" && !r.valid_to
      ? el(
          "p",
          { class: "small flag" },
          `保留（文脈に入れていない）: ${r.hold_reasons.map(reasonText).join("、")}`,
        )
      : "",
  );
}

function unavailableText(code: string): string {
  return code === "self_unavailable"
    ? "まだ使えません（表・権限の適用前）。"
    : code === "voice_device_not_allowed"
      ? "この端末からは見られません。"
      : `読めませんでした（${code}）。`;
}

export async function renderSelfPanel(root: HTMLElement, onClose?: () => void): Promise<void> {
  let view: SelfView;
  try {
    view = await api<SelfView>("/v1/self");
  } catch (e) {
    const code = e instanceof ApiError ? e.code : String(e);
    root.replaceChildren(
      el(
        "section",
        { class: "card self" },
        el("h2", {}, "Jarvis のメモ"),
        el("p", { class: "small muted" }, unavailableText(code)),
        closeButton(root, onClose),
      ),
    );
    return;
  }
  const status = el("p", { class: "small muted" });
  const send = async (path: string, body: Record<string, unknown>, btn?: HTMLButtonElement) => {
    if (btn) btn.disabled = true;
    status.textContent = "反映しています…";
    try {
      await api(path, body);
      await renderSelfPanel(root, onClose);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : String(e);
      const msg: Record<string, string> = {
        passkey_session_required: "パスキーでログインした端末からだけ操作できます。",
        milestone_cannot_be_deleted: "節目は消せません（事実の記録です）。",
        stale: "すでに変わっていました。",
        frozen: "今は止まっています（凍結中）。",
      };
      status.textContent = msg[code] ?? `反映できませんでした（${code}）`;
      if (btn) btn.disabled = false;
    }
  };
  const act = (label: string, body: Record<string, unknown>, confirmText?: string) => {
    const b = el("button", {}, label) as HTMLButtonElement;
    b.onclick = () => {
      if (confirmText && !window.confirm(confirmText)) return;
      void send("/v1/self/action", body, b);
    };
    return b;
  };
  const fold = (r: Row, body: HTMLElement) =>
    r.domain === "health"
      ? el(
          "details",
          { class: "health" },
          el("summary", {}, "健康に関するもの（開くと表示）"),
          body,
        )
      : body;

  /** 1 行と、その行のボタン */
  const item = (target: Target, r: Row, history: Row[] = []) => {
    const buttons = el("div", { class: "row wrap" });
    if (!r.valid_to) {
      if (r.state === "held") {
        buttons.append(act("使ってよい", { action: "release", target, id: r.id }));
        buttons.append(act("使わない（消す）", { action: "delete", target, id: r.id }));
      } else if (r.kind !== "milestone")
        buttons.append(
          act(target === "wish" ? "やめて（消す）" : "消す", {
            action: "delete",
            target,
            id: r.id,
          }),
        );
    } else if (!history.some((h) => !h.valid_to))
      buttons.append(act("戻す", { action: "restore", target, id: r.id }));
    const body = el("div", { class: "self-item" }, rowBody(r), buttons);
    const older = history.filter((h) => h.id !== r.id);
    if (older.length)
      body.append(
        el(
          "details",
          { class: "history" },
          el("summary", {}, `前の版・閉じた版 ${older.length} 件`),
          ...older.map((h) =>
            el(
              "div",
              { class: "self-item old" },
              rowBody(h),
              // 開いている版があれば、それを閉じてからこの版の写しを足す（Gateway が 1 文で行う）
              el(
                "div",
                { class: "row" },
                act("この版に戻す", { action: "restore", target, id: h.id }),
              ),
            ),
          ),
        ),
      );
    return fold(r, body);
  };
  /** 同じ鍵の版をまとめ、開いている版（無ければ最も新しい版）を先頭に */
  const grouped = (target: Target, rows: Row[]) => {
    const keys = new Map<string, Row[]>();
    for (const r of rows) keys.set(r.key, [...(keys.get(r.key) ?? []), r]);
    const open: HTMLElement[] = [];
    const held: HTMLElement[] = [];
    const closed: HTMLElement[] = [];
    for (const list of keys.values()) {
      const head = list.find((r) => !r.valid_to) ?? (list[0] as Row);
      const node = item(target, head, list);
      if (head.valid_to) closed.push(node);
      else if (head.state === "held") held.push(node);
      else open.push(node);
    }
    return { open, held, closed };
  };

  const sw = (key: string, label: string) => {
    const on = view.switches?.[key] ?? true;
    const b = el(
      "button",
      { class: "link", type: "button", "aria-pressed": String(on) },
      `${label}: ${on ? "入" : "切"}`,
    ) as HTMLButtonElement;
    b.onclick = () => void send("/v1/self/switches", { key, value: !on }, b);
    return b;
  };

  const notes = grouped("note", view.notes);
  const journal = grouped("journal", view.journal);
  const wishes = grouped("wish", view.wishes);
  const inner = grouped("inner", view.inner);
  const notices: string[] = [];
  if (!view.enabled)
    notices.push("今は止まっています（設定 config/self.yaml の enabled が false）。");
  if (!view.watch_loaded)
    notices.push(
      "見張りの決まりがまだ入っていないので、新しいメモは全て保留になります（使うかは本人が決めます）。",
    );
  const c = view.last_night?.collect;
  if (c?.applied) {
    const a = c.applied;
    notices.push(
      `昨夜（${view.last_night?.run_date}）: メモを ${a.note_add ?? 0} 件足し、${a.note_revise ?? 0} 件直し、${a.note_retire ?? 0} 件消しました（保留 ${c.held_ops ?? 0} 件）。`,
    );
  } else if (view.last_night?.self?.status === "skipped")
    notices.push(`昨夜の振り返りは出していません（${view.last_night.self.reason ?? ""}）。`);

  const dateInput = el("input", { type: "date" }) as HTMLInputElement;
  const asOf = el("button", {}, "この日の自分に戻す") as HTMLButtonElement;
  asOf.onclick = () => {
    if (!dateInput.value) return;
    const at = new Date(`${dateInput.value}T23:59:59+09:00`).toISOString();
    if (
      !window.confirm(
        `${dateInput.value} の終わりの時点のメモに戻します（今のメモは閉じます。あとで戻せます）。`,
      )
    )
      return;
    void send("/v1/self/action", { action: "restore_as_of", at }, asOf);
  };
  const section = (title: string, ...children: (Node | string)[]) =>
    el("div", { class: "self-section" }, el("h3", {}, title), ...children);
  const closedFold = (list: HTMLElement[], label: string) =>
    list.length
      ? el("details", { class: "closed" }, el("summary", {}, `${label} ${list.length} 件`), ...list)
      : "";
  const innerByDay = new Map<string, HTMLElement[]>();
  for (const r of view.inner.filter((x) => !x.valid_to)) {
    const d = r.note_date ?? "";
    innerByDay.set(d, [...(innerByDay.get(d) ?? []), item("inner", r)]);
  }

  root.replaceChildren(
    el(
      "section",
      { class: "card self" },
      el(
        "div",
        { class: "row self-head" },
        el("h2", {}, "Jarvis のメモ"),
        closeButton(root, onClose),
      ),
      el(
        "p",
        { class: "small muted" },
        `Jarvis が夜の振り返りで自分の経験から書いたものです。返事には新しい順に ${view.max_in_context} 件まで「これまでの自分の気づき」として入ります（本人の言葉・設定・直近の会話の方が優先）。直すことはできません。伝えたいことは会話で話してください。`,
      ),
      ...notices.map((n) => el("p", { class: "small" }, n)),
      el(
        "div",
        { class: "row wrap" },
        sw("self.reflect", "夜の振り返り"),
        sw("self.context", "返事に使う"),
      ),
      section(
        "自分についてのメモ",
        ...(notes.open.length
          ? notes.open
          : [el("p", { class: "small muted" }, "まだありません。")]),
        ...(notes.held.length ? [el("h4", {}, "保留（文脈に入れていない）"), ...notes.held] : []),
        closedFold(notes.closed, "閉じたメモ"),
      ),
      section(
        "自伝",
        ...(journal.open.length
          ? journal.open
          : [el("p", { class: "small muted" }, "まだありません。")]),
        ...journal.held,
        closedFold(journal.closed, "閉じた出来事"),
      ),
      section(
        "やりたいこと（何も自動では動きません）",
        ...(wishes.open.length
          ? wishes.open
          : [el("p", { class: "small muted" }, "まだありません。")]),
        ...wishes.held,
        closedFold(wishes.closed, "閉じたもの"),
      ),
      view.inner_enabled || view.inner.length
        ? section(
            "昼の 1 行（返事の最後に Jarvis が自分のために残したもの）",
            ...[...innerByDay.entries()].flatMap(([d, items]) => [
              el("p", { class: "small muted" }, d),
              ...items,
            ]),
            closedFold(inner.closed, "消したもの"),
          )
        : "",
      section(
        "まとめて",
        el(
          "div",
          { class: "row wrap" },
          act(
            "全部消す",
            { action: "reset_all" },
            "Jarvis のメモ・自伝の出来事・やりたいこと・昼の 1 行を全部閉じます（あとで日付を選んで戻せます）。",
          ),
          act(
            "声から作ったものを全部消す",
            { action: "delete_by_source", source: "voice", mode: "any" },
            "出典に声の端末の会話を含むものを全部閉じます（戻せます）。",
          ),
        ),
        el("div", { class: "row wrap" }, dateInput, asOf),
      ),
      status,
    ),
  );
}

function closeButton(root: HTMLElement, onClose?: () => void): HTMLElement {
  const b = el("button", { class: "link", type: "button" }, "閉じる") as HTMLButtonElement;
  b.onclick = () => {
    root.replaceChildren();
    onClose?.();
  };
  return b;
}

/**
 * 「状況」の引き出しの「Jarvis のメモ」の入口（ADR-0055）: いちばん新しい有効なメモを 1 つと「全部読む」のボタン。
 * 押すと同じ場所に画面の全部（renderSelfPanel）を出し、閉じると入口に戻る。main.ts からは 1 行で呼ぶ
 */
export async function mountSelfEntry(root: HTMLElement): Promise<void> {
  const open = el("button", { class: "link", type: "button" }, "全部読む") as HTMLButtonElement;
  open.onclick = () => void renderSelfPanel(root, () => void mountSelfEntry(root));
  let view: SelfView | null = null;
  let code = "";
  try {
    view = await api<SelfView>("/v1/self");
  } catch (e) {
    code = e instanceof ApiError ? e.code : String(e);
  }
  if (!view) {
    root.replaceChildren(
      el("div", { class: "self-teaser" }, el("p", { class: "small muted" }, unavailableText(code))),
    );
    return;
  }
  const latest = [...view.notes]
    .filter((r) => !r.valid_to && r.state === "active" && r.domain !== "health")
    .sort((a, b) => b.valid_from.localeCompare(a.valid_from))[0];
  const held = view.notes.filter((r) => !r.valid_to && r.state === "held").length;
  const meta = [
    latest ? `${md(latest.valid_from)} の振り返りから` : "",
    held ? `保留 ${held} 件` : "",
    !view.enabled ? "夜の振り返りは止まっています" : "",
  ].filter(Boolean);
  root.replaceChildren(
    el(
      "div",
      { class: "self-teaser" },
      latest
        ? el("p", { class: "quote" }, `「${latest.text}」`)
        : el("p", { class: "small muted" }, "まだメモはありません。"),
      el(
        "div",
        { class: "row self-teaser-foot" },
        el("span", { class: "small muted" }, meta.join(" · ")),
        open,
      ),
    ),
  );
}
